import {
  ArrowDownIcon,
  ArrowUpIcon,
  Collapsible,
  Typography,
} from '@atlas-design-system/react'
import { ReactNode, useCallback } from 'react'

import styles from './collapsible-form-block.module.css'

type CollapsibleFormBlockProps = {
  children?: ReactNode
  title?: string | ReactNode
  defaultOpened?: boolean
  className?: string
}

type CollapsibleHeaderArgs = {
  tabIndex: number
  isExpanded: boolean
  onClick: () => void
  onKeyDown: () => void
}

type CollapsibleFormBlockHeaderProps = CollapsibleHeaderArgs & {
  title: CollapsibleFormBlockProps['title']
  className?: string
}

const getCollapsibleTestId = (title: CollapsibleFormBlockProps['title']) => {
  if (typeof title === 'string' || typeof title === 'number') {
    return `collapsible-block-${String(title).replaceAll(/\s+/g, '')}`
  }

  return 'collapsible-block-custom-title'
}

const CollapsibleFormBlockHeader: React.FC<CollapsibleFormBlockHeaderProps> = ({
  onClick,
  tabIndex,
  onKeyDown,
  isExpanded,
  title,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
      className={`${styles.header} ${className ?? ''}`}
    >
      <Typography variant="displayText6">{title}</Typography>
      {isExpanded ? (
        <ArrowUpIcon className={styles.arrowIcon} width="20" />
      ) : (
        <ArrowDownIcon className={styles.arrowIcon} width="20" />
      )}
    </button>
  )
}

export const CollapsibleFormBlock: React.FC<CollapsibleFormBlockProps> = ({
  children,
  title = 'Ancillary Builder',
  defaultOpened = false,
  className,
}) => {
  const renderHeader = useCallback(
    (headerProps: CollapsibleHeaderArgs) => {
      return (
        <CollapsibleFormBlockHeader
          {...headerProps}
          title={title}
          className={className}
        />
      )
    },
    [className, title],
  )

  return (
    <Collapsible
      data-testid={getCollapsibleTestId(title)}
      header={renderHeader}
      defaultOpened={defaultOpened}
      className={styles.wrapper}
    >
      {children}
    </Collapsible>
  )
}
